import { SearchResultsStore } from 'stores/search-results-store';

import { DefaultRequestFields } from 'models';

import {
  FlightsAdvancedFilterType,
  FlightsTripType,
  LodgingPwaPropertySearch,
  OperationResult,
  TravelerDetailsType,
} from '__generated__/api';
import { getLocKeyFromBexApiDateTime } from 'components/multi-item/bex-api-date-time-to-time-loc-key';

export const getDefaultRequestFields = (
  searchCriteria: LodgingPwaPropertySearch.SearchCriteria,
  multiItemId: string,
  bexApiResults: OperationResult<LodgingPwaPropertySearch.Query>
): DefaultRequestFields | null => ({
  ...mapDefaultRequestFields(
    {
      startDate: getDateStringFromDateRange(searchCriteria.dateRange.checkInDate),
      endDate: getDateStringFromDateRange(searchCriteria.dateRange.checkOutDate),
    },
    searchCriteria
  ),
  ...mapSortAndFiltersRequestFields(searchCriteria, bexApiResults),
  ...mapMultiItemRequestFields(multiItemId, bexApiResults),
});

/**
 * Javascript Date objects require a month index which is why we need minus 1
 * TODO: Add date string logic to the BEX API SDK
 */
const getDateStringFromDateRange = (dateRange: LodgingPwaPropertySearch.CheckInDate | LodgingPwaPropertySearch.CheckOutDate): string =>
  new Date(Date.UTC(dateRange.year, dateRange.month - 1, dateRange.day)).toISOString().split('T')[0];

const mapDefaultRequestFields = (
  bffDefaultRequestFields: DefaultRequestFields,
  searchCriteria: LodgingPwaPropertySearch.SearchCriteria
): DefaultRequestFields => {
  let defaultRequestFields: DefaultRequestFields = { ...bffDefaultRequestFields };

  if (searchCriteria.destination !== null) {
    const destination = searchCriteria.destination;
    if (destination && destination.regionId !== null) {
      defaultRequestFields.regionId = destination.regionId;
    }
    if (destination && destination.regionName !== null) {
      defaultRequestFields.destination = destination.regionName;
    }
  }

  if (searchCriteria.searchIntent) {
    defaultRequestFields = {
      ...defaultRequestFields,
      ...searchCriteria.searchIntent,
    };
  }

  return defaultRequestFields;
};

const mapSortAndFiltersRequestFields = (
  searchCriteria: LodgingPwaPropertySearch.SearchCriteria,
  bexApiResults: OperationResult<LodgingPwaPropertySearch.Query> = null
): DefaultRequestFields => {
  const defaultRequestFields: DefaultRequestFields = {};
  if (bexApiResults?.data?.propertySearch?.searchCriteria?.propertyShopOptions?.points) {
    defaultRequestFields.useRewards = SearchResultsStore.GetDefaultUseRewards(bexApiResults);
  }

  if (searchCriteria.sort !== null) {
    defaultRequestFields.sort = searchCriteria.sort;
  }

  Object.keys(searchCriteria.filters).forEach(filter => {
    const filterVal = searchCriteria.filters[filter];
    if (filterVal !== null && filterDict[filter] !== null) {
      // temporary hack until universal sort and filter. see: https://expedia.slack.com/archives/GJY77CBQX/p1567010905012000
      if (filter === 'reviewScore') {
        defaultRequestFields[filterDict[filter]] = (filterVal.min * 10).toString();
      } else if (Array.isArray(filterVal) && filterVal.length > 0) {
        defaultRequestFields[filterDict[filter]] = filterVal.map(x => x.toString());
      } else if (typeof filterVal === 'string') {
        defaultRequestFields[filterDict[filter]] = filterVal;
      }
    }
  });

  return defaultRequestFields;
};

const filterDict = {
  sort: 'sort',
  accessibility: 'accessibility',
  amenities: 'amenities',
  bedroomFilter: 'bedroomFilter',
  deals: 'deals',
  propertyName: 'hotelName',
  propertyStyles: 'propertyStyle',
  structureTypes: 'lodging',
  paymentType: 'paymentType',
  poi: 'poi',
  priceBuckets: 'price',
  reviewScore: 'guestRating',
  starList: 'star',
  travelerType: 'travelerType',
  mealPlan: 'mealPlan',
  cleaningAndSafetyPractices: 'cleaningAndSafetyPractices',
  neighborhood: 'neighborhood',
  stayType: 'stayType',
};

const mapMultiItemRequestFields = (
  multiItemId: string,
  bexApiResults: OperationResult<LodgingPwaPropertySearch.Query> = null
): DefaultRequestFields => {
  const defaultRequestFields: DefaultRequestFields = {};
  if (multiItemId) {
    defaultRequestFields.misId = multiItemId;
  }

  if (bexApiResults) {
    const primaryFlight = bexApiResults.data.propertySearch.multiItemSearchContext?.flights?.[0]?.primary;
    if (primaryFlight) {
      defaultRequestFields.origin = primaryFlight.journeyCriterias[0].origin;
      defaultRequestFields.cabinClass = primaryFlight.searchPreferences?.cabinClass;
      defaultRequestFields.tripType = primaryFlight.tripType;
      defaultRequestFields.airlineCode = primaryFlight.searchPreferences?.airline;
      defaultRequestFields.directFlights = String(
        primaryFlight.searchPreferences?.advancedFilters?.includes(FlightsAdvancedFilterType.NONSTOP)
      );

      defaultRequestFields.infantsInSeats =
        primaryFlight.travelers.filter(traveler => traveler.type === TravelerDetailsType.INFANT_IN_SEAT).length > 0 ? '1' : '0';

      if (primaryFlight.journeyCriterias.length) {
        const { checkInDate, checkOutDate } = bexApiResults.data.propertySearch.searchCriteria.dateRange;
        const startDate = getDateStringFromDateRange(checkInDate);
        const endDate = getDateStringFromDateRange(checkOutDate);

        const isOneWay = primaryFlight.tripType === FlightsTripType.ONE_WAY;
        const tripStartDate = getDateStringFromDateRange(primaryFlight.journeyCriterias[0].departureDate);
        const tripEndDate = isOneWay ? endDate : getDateStringFromDateRange(primaryFlight.journeyCriterias[1].departureDate);
        if (startDate !== tripStartDate || tripEndDate !== endDate) {
          defaultRequestFields.partialStay = 'true';
          defaultRequestFields.partialStayStartDate = startDate;
          defaultRequestFields.partialStayEndDate = endDate;
          defaultRequestFields.startDate = tripStartDate;
          defaultRequestFields.endDate = tripEndDate;
        } else {
          defaultRequestFields.partialStay = 'false';
        }
      }
    }

    const primaryCar = bexApiResults.data.propertySearch.multiItemSearchContext?.cars?.[0]?.primary;
    if (primaryCar) {
      defaultRequestFields.pickUpTime = getLocKeyFromBexApiDateTime(primaryCar.pickUpDateTime);
      defaultRequestFields.dropOffTime = getLocKeyFromBexApiDateTime(primaryCar.dropOffDateTime);
    }
  }

  return defaultRequestFields;
};
